import { createTheme } from '@mui/material/styles';
import { LENA_COLORS, THEME, NEUTRAL_COLORS, SYSTEM_COLORS, GRADIENTS, SHADOWS, TEXT_COLORS } from './colors';

/**
 * ============================================================================
 * LENA - TEMA MATERIAL-UI
 * ============================================================================
 *
 * Design de alto padrão inspirado no Apple com cores da Eurofarma Lena
 *
 * COMO PERSONALIZAR:
 * - Para mudar as cores: edite /theme/colors.js
 * - Para mudar ACTIVE_THEME: edite a constante em colors.js
 * - Temas disponíveis: 'green-header', 'blue-header', 'white-header', 'gradient'
 *
 * ============================================================================
 */

// Criar tema base
let theme = createTheme({
    palette: {
        mode: 'light',

        // Cores primárias (do tema ativo)
        primary: {
            main: THEME.primary.main,
            light: THEME.primary.light,
            dark: THEME.primary.dark,
            darker: THEME.primary.darker,
            contrastText: THEME.primary.contrastText,
        },

        // Cores secundárias (do tema ativo)
        secondary: {
            main: THEME.secondary.main,
            light: THEME.secondary.light,
            dark: THEME.secondary.dark,
            contrastText: THEME.secondary.contrastText,
        },

        // Cores neutras
        neutral: NEUTRAL_COLORS,

        // Compatibilidade com DMS components
        blue: {
            main: THEME.header.background,
            light: THEME.header.background,
            dark: THEME.header.border,
        },

        sidebar: {
            main: THEME.sidebar.background,
            light: THEME.sidebar.hover,
            dark: THEME.sidebar.border,
        },

        // Cores de status
        error: {
            main: SYSTEM_COLORS.error,
            light: '#FFE5E5',
            dark: '#D32F2F',
        },

        warning: {
            main: SYSTEM_COLORS.warning,
            light: '#FFF4E5',
            dark: '#E68900',
        },

        info: {
            main: SYSTEM_COLORS.info,
            light: LENA_COLORS.brand.bluePale,
            dark: LENA_COLORS.brand.blueDark,
        },

        success: {
            main: SYSTEM_COLORS.success,
            light: LENA_COLORS.brand.greenPale,
            dark: LENA_COLORS.brand.greenDark,
        },

        // Backgrounds
        background: {
            default: NEUTRAL_COLORS.white,
            paper: NEUTRAL_COLORS.white,
            secondary: NEUTRAL_COLORS.gray100,
        },

        // Textos
        text: {
            primary: TEXT_COLORS.primary,
            secondary: TEXT_COLORS.secondary,
            disabled: TEXT_COLORS.disabled,
        },

        // Divisores
        divider: NEUTRAL_COLORS.gray200,
    },

    // Tipografia estilo Apple
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),

        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: TEXT_COLORS.primary,
        },

        h2: {
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: TEXT_COLORS.primary,
        },

        h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: TEXT_COLORS.primary
        },

        h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: TEXT_COLORS.primary,
        },

        h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: TEXT_COLORS.primary,
        },

        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            color: TEXT_COLORS.primary
        },

        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            color: TEXT_COLORS.secondary,
        },

        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: TEXT_COLORS.secondary,
        },

        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.6,
            color: TEXT_COLORS.primary,
        },

        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.6,
            color: TEXT_COLORS.secondary,
        },

        button: {
            fontSize: '0.9375rem',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.01em',
        },

        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            color: TEXT_COLORS.tertiary,
        },

        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: TEXT_COLORS.tertiary,
        },
    },

    // Bordas arredondadas
    shape: {
        borderRadius: 8,
    },

    // Sombras personalizadas
    shadows: [
        'none',
        SHADOWS.xs,
        SHADOWS.sm,
        SHADOWS.sm,
        SHADOWS.md,
        SHADOWS.md,
        SHADOWS.md,
        SHADOWS.md,
        SHADOWS.lg,
        SHADOWS.lg,
        SHADOWS.lg,
        SHADOWS.lg,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
        SHADOWS.xl,
    ],
});

// Tema final com customizações adicionais
export const appTheme = createTheme(theme, {
    // Tipografia customizada (propriedades extras)
    typography: {
        link: {
            fontSize: '0.9rem',
            fontWeight: 500,
            color: TEXT_COLORS.link,
            cursor: 'pointer',
            transition: 'color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
            '&:hover': {
                color: TEXT_COLORS.linkHover,
            },
        },
        cardTitle: {
            fontSize: '1.125rem',
            fontWeight: 600,
            color: TEXT_COLORS.primary,
        },
        h7: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: TEXT_COLORS.primary,
        },
        h8: {
            fontSize: '0.75rem',
            fontWeight: 500,
            color: TEXT_COLORS.secondary,
        },
    },

    // Layout customizado
    layout: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            bgcolor: NEUTRAL_COLORS.gray100,
        },
        header: {
            boxShadow: 'none',
            zIndex: 1200,
            width: '100vw',
            position: 'sticky',
            top: 0,
            background: THEME.header.background,
            color: THEME.header.text,
            borderBottom: `1px solid ${THEME.header.border}`,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        contentWrapper: {
            display: 'flex',
            flexGrow: 1,
            width: '100%',
        },
        sidebar: {
            boxShadow: 'none',
            borderRight: `1px solid ${THEME.sidebar.border}`,
            zIndex: 100,
            position: 'relative',
            width: 250,
            bgcolor: THEME.sidebar.background,
            transition: 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        sidebarCollapsed: {
            width: 80,
            position: 'static',
            bgcolor: THEME.sidebar.background,
            transition: 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
        sidebarCollapsedMobile: {
            display: 'none',
        },
        mainContent: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            minHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
            bgcolor: NEUTRAL_COLORS.gray100,
            padding: theme.spacing(3),
        },
        footer: {
            marginTop: 2,
        },
    },

    // Componentes customizados
    components: {

        MuiCssBaseline: {
            styleOverrides: {
                '[data-variant="dms-alt-box-header"] .MuiTypography-root': {
                    color: NEUTRAL_COLORS.gray100,
                },
                '.MuiIconButton-root .MuiTypography-body1': {
                    color: NEUTRAL_COLORS.gray100,
                },
            },
        },

        // Botões
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 10,
                    fontWeight: 500,
                    padding: '10px 24px',
                    fontSize: '0.9375rem',
                    boxShadow: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: SHADOWS.sm,
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                },
                contained: {
                    boxShadow: SHADOWS.xs,
                    '&:hover': {
                        boxShadow: SHADOWS.md,
                    },
                },
                containedPrimary: {
                    background: GRADIENTS.primary,
                    '&:hover': {
                        background: THEME.primary.dark,
                    },
                },
                outlined: {
                    borderWidth: 1.5,
                    borderColor: NEUTRAL_COLORS.gray300,
                    '&:hover': {
                        borderWidth: 1.5,
                        borderColor: THEME.primary.main,
                        backgroundColor: `${THEME.primary.main}08`,
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: NEUTRAL_COLORS.gray100,
                    },
                },
                sizeLarge: {
                    padding: '12px 32px',
                    fontSize: '1rem',
                },
                sizeSmall: {
                    padding: '6px 16px',
                    fontSize: '0.875rem',
                },
            },
        },

        // Cards
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: SHADOWS.sm,
                    border: `1px solid ${NEUTRAL_COLORS.gray200}`,
                    backgroundColor: NEUTRAL_COLORS.white,
                    transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                        boxShadow: SHADOWS.md,
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },

        // Inputs
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        backgroundColor: NEUTRAL_COLORS.white,
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '& fieldset': {
                            borderColor: NEUTRAL_COLORS.gray300,
                            borderWidth: 1.5,
                        },
                        '&:hover fieldset': {
                            borderColor: THEME.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: THEME.primary.main,
                            borderWidth: 2,
                        },
                        '&.Mui-error fieldset': {
                            borderColor: SYSTEM_COLORS.error,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: TEXT_COLORS.secondary,
                        fontWeight: 500,
                        '&.Mui-focused': {
                            color: THEME.primary.main,
                        },
                    },
                },
            },
        },

        // AppBar (Header)
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: 'linear-gradient(to right, #ffffff, #0033a0)',
                    color: THEME.header.text,
                    boxShadow: 'none',
                    borderBottom: `1px solid ${THEME.header.border}`,
                    backdropFilter: 'blur(10px)',
                    '& .MuiIconButton-root': {
                        color: THEME.header.text,
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: THEME.header.hover,
                            transform: 'scale(1.05)',
                        },
                    },
                    '& .MuiSvgIcon-root': {
                        color: THEME.header.text,
                    },
                },
            },
        },

        // Drawer (Sidebar)
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: THEME.sidebar.background,
                    borderRight: `1px solid ${THEME.sidebar.border}`,
                    boxShadow: 'none',
                },
            },
        },

        // List Items (Sidebar)
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    margin: '4px 12px',
                    padding: '12px 16px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    color: THEME.sidebar.text,
                    fontWeight: 500,
                    '&:hover': {
                        backgroundColor: THEME.sidebar.hover,
                        transform: 'scale(0.98)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: THEME.sidebar.activeBackground,
                        color: THEME.sidebar.active,
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: THEME.sidebar.activeBackground,
                        },
                        '& .MuiListItemIcon-root': {
                            color: THEME.sidebar.active,
                        },
                    },
                },
            },
        },

        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: THEME.sidebar.textSecondary,
                    minWidth: 40,
                    transition: 'color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                },
            },
        },

        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                },
            },
        },

        // Icon Buttons
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: NEUTRAL_COLORS.gray100,
                        transform: 'scale(1.05)',
                    },
                },
            },
        },

        // Tooltips
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: NEUTRAL_COLORS.gray800,
                    fontSize: '0.875rem',
                    padding: '8px 12px',
                    borderRadius: 8,
                    boxShadow: SHADOWS.md,
                },
                arrow: {
                    color: NEUTRAL_COLORS.gray800,
                },
            },
        },

        // Paper
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    // '& .MuiTypography-root': {
                    //     color: NEUTRAL_COLORS.gray100,
                    // },
                },
                elevation1: {
                    boxShadow: SHADOWS.sm,
                },
                elevation2: {
                    boxShadow: SHADOWS.md,
                },
                elevation3: {
                    boxShadow: SHADOWS.lg,
                },
            },
        },

        // Divider
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: NEUTRAL_COLORS.gray200,
                },
            },
        },

        // Chip
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
                // filled: {
                //     backgroundColor: NEUTRAL_COLORS.gray100,
                // },
            },
        },

        // Switch
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME.primary.main,
                        '& + .MuiSwitch-track': {
                            backgroundColor: THEME.primary.main,
                        },
                    },
                },
            },
        },

        // Checkbox & Radio
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: NEUTRAL_COLORS.gray400,
                    '&.Mui-checked': {
                        color: THEME.primary.main,
                    },
                },
            },
        },

        MuiRadio: {
            styleOverrides: {
                root: {
                    color: NEUTRAL_COLORS.gray400,
                    '&.Mui-checked': {
                        color: THEME.primary.main,
                    },
                },
            },
        },

        // Table
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: NEUTRAL_COLORS.gray200,
                },
                head: {
                    fontWeight: 600,
                    color: TEXT_COLORS.secondary,
                    backgroundColor: NEUTRAL_COLORS.gray100,
                },
            },
        },

        // Alert
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: SHADOWS.sm,
                },
                standardSuccess: {
                    backgroundColor: LENA_COLORS.brand.greenPale,
                    color: LENA_COLORS.brand.greenDark,
                },
                standardError: {
                    backgroundColor: '#FFE5E5',
                    color: '#D32F2F',
                },
                standardWarning: {
                    backgroundColor: '#FFF4E5',
                    color: '#E68900',
                },
                standardInfo: {
                    backgroundColor: LENA_COLORS.brand.bluePale,
                    color: LENA_COLORS.brand.blueDark,
                },
            },
        },

        // Dialog
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    boxShadow: SHADOWS.xl,
                },
            },
        },
    },
});

// Export colors for direct use
export { LENA_COLORS, THEME as ACTIVE_THEME_CONFIG };

// Maintain compatibility with existing code using APP_COLORS
export const APP_COLORS = {
    // Primary colors - Using active theme
    primary: THEME.primary,

    // Secondary colors
    secondary: THEME.secondary,

    // Login colors
    login: THEME.login,

    // Neutral palette - Apple's sophisticated grays
    neutral: NEUTRAL_COLORS,

    // System colors - Apple style
    system: SYSTEM_COLORS,

    // Sidebar - Clean and modern (Apple style)
    sidebar: THEME.sidebar,

    // Header - Apple style
    header: THEME.header,

    // Gradients - Subtle and elegant
    gradients: GRADIENTS,

    // Background colors
    background: {
        default: NEUTRAL_COLORS.white,
        secondary: NEUTRAL_COLORS.gray100,
        elevated: NEUTRAL_COLORS.white,
        dark: NEUTRAL_COLORS.gray900,
    },

    // Text colors - Apple's hierarchy
    text: TEXT_COLORS,

    // Status colors - Apple system colors
    status: SYSTEM_COLORS,

    // Elevation shadows - Apple style
    elevation: SHADOWS,

    // Brand colors (direct access)
    brand: LENA_COLORS.brand,
};

export default appTheme;
